import { useQuery } from '@tanstack/react-query'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { priorityCountsApiResponseSchema, PriorityCountsApiResponse } from '../../types/sentryIssue.type'
import {
  isDispatchMobileProject,
  isDockAutomationProject,
  SENTRY_PROJECT_APEX_CLIENT,
} from '../../constants/sentryProjects'
import { getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'
import { sentryQueryKeys } from './queryKeys'

interface UsePriorityCountsParams {
  startDate: string
  endDate: string
  project?: string
  workstream?: string | null
  environment?: string | null
  /** For Dispatch Mobile: optional title search (query=title:"...") */
  searchQuery?: string
}

/**
 * Custom hook to fetch priority counts from the Sentry API
 * This hook fetches aggregated counts of issues by priority level (high, medium, low)
 *
 * @param params - Query parameters including date range, project, and workstream
 * @returns React Query result with priority counts data
 */
export const usePriorityCounts = ({
  startDate,
  endDate,
  project = SENTRY_PROJECT_APEX_CLIENT,
  workstream = null,
  environment = null,
  searchQuery,
}: UsePriorityCountsParams) => {
  const searchQueryNorm = searchQuery?.trim() || null
  return useQuery({
    queryKey: sentryQueryKeys.priorityCounts({
      projectId: project,
      startDate,
      endDate,
      workstream,
      environment,
      searchQuery: searchQueryNorm,
    }),
    enabled: !!startDate && !!endDate, // Only run query when dates are available
    queryFn: async (): Promise<PriorityCountsApiResponse> => {
      // Build query params
      const params = new URLSearchParams()

      // Parse the date strings to check if it's the default 24-hour range (yesterday to today)
      const startDateObj = new Date(startDate)
      const endDateObj = new Date(endDate)

      // Check if it's exactly yesterday to today (default range)
      // Only use statsPeriod=24h for the default range, not for single date selections
      const isSameDate =
        startDateObj.getFullYear() === endDateObj.getFullYear() &&
        startDateObj.getMonth() === endDateObj.getMonth() &&
        startDateObj.getDate() === endDateObj.getDate()

      const is24HoursRange =
        !isSameDate && Math.abs(endDateObj.getTime() - startDateObj.getTime()) <= 24 * 60 * 60 * 1000

      // Add statsPeriod only for yesterday-to-today range, otherwise use explicit start/end dates
      if (is24HoursRange) {
        params.append('statsPeriod', '24h')
      } else {
        // Format dates: start at 00:00:00, end at 23:59:59
        const startYear = startDateObj.getFullYear()
        const startMonth = String(startDateObj.getMonth() + 1).padStart(2, '0')
        const startDay = String(startDateObj.getDate()).padStart(2, '0')
        const formattedStart = `${startYear}-${startMonth}-${startDay}T00:00:00`
        params.append('start', formattedStart)

        const endYear = endDateObj.getFullYear()
        const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0')
        const endDay = String(endDateObj.getDate()).padStart(2, '0')
        const formattedEnd = `${endYear}-${endMonth}-${endDay}T23:59:59`
        params.append('end', formattedEnd)
      }

      params.append('project', project)

      if (environment && environment !== 'all') {
        params.append('environment', environment)
      }

      if (isDispatchMobileProject(project) || isDockAutomationProject(project)) {
        // Always send query parameter for Dispatch Mobile and Dock Automation
        // If searchQuery is provided, use title search; otherwise send empty query
        if (searchQueryNorm) {
          params.append('query', `title:"${searchQueryNorm}"`)
        } else {
          params.append('query', '')
        }
      } else {
        // Apex Client logic
        if (workstream !== null && workstream !== 'all') {
          // Workstream filter provided (e.g., from SentryDashboard)
          const culpritQuery = getWorkstreamCulpritQuery(workstream)
          if (culpritQuery) {
            params.append('query', culpritQuery)
          }
        } else {
          // No workstream filter (e.g., Overview page) - send empty query
          params.append('query', '')
        }
      }

      const apiEndpoint = `${apiUrls.getPriorityCounts}?${params.toString()}`
      const response = await http.get(apiEndpoint)
      const data = response.data
      // Validate response with Zod schema
      const parsed = priorityCountsApiResponseSchema.parse(data)
      return parsed
    },
    // Refetch when window regains focus
    refetchOnWindowFocus: false,
    // Cache data for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  })
}
